import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./creator.css";
import { api, fmtDate } from "./api";
import CreatorPass from "./CreatorPass";
import HaileyPanel from "./HaileyPanel";
import { loadTikTokPixel, ttTrack } from "./tiktok";

export function CreatorBar({ right }) {
  return (
    <header className="cp-bar">
      <div className="cp-wrap">
        <div className="cp-bar-left">
          <span className="cp-logo">
            <Link to="/" className="cp-logo-home" title="Back to InstaPass" aria-label="InstaPass home">INSTAPASS</Link>{" "}
            <Link to="/creators"><span>CREATORS</span></Link>
          </span>
        </div>
        {right}
      </div>
    </header>
  );
}

export function CreatorFooter() {
  return (
    <footer className="cp-footer">
      <div className="cp-wrap">
        InstaPass Creator Partners is a commission program. Commissions are earned only on verified, qualifying ticket purchases
        made through your links and are not guaranteed. Creators must clearly disclose that they earn a commission.
        Referral QR codes are marketing links, not admission tickets. <Link to="/">InstaPass home</Link> ·{" "}
        <Link to="/terms&conditions">Terms</Link> ·{" "}
        <Link to="/privacyPolicy">Privacy</Link>
      </div>
    </footer>
  );
}

// Keep UTM tags from the TikTok ad so the application can be tied to the campaign.
function rememberUtm(search) {
  const p = new URLSearchParams(search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "ttclid"].forEach((k) => p.get(k) && (utm[k] = p.get(k).slice(0, 100)));
  if (Object.keys(utm).length) {
    try {
      sessionStorage.setItem("instapass_creator_utm", JSON.stringify(utm));
    } catch {}
  }
}

export default function CreatorLanding() {
  const location = useLocation();
  const [program, setProgram] = useState(null);

  useEffect(() => {
    document.title = "InstaPass Creator Partners — your influence, your earnings";
    rememberUtm(location.search);
    loadTikTokPixel();
    ttTrack("ViewContent");
    api("/program", { auth: false })
      .then(setProgram)
      .catch(() => setProgram({ events: [], tierRates: [] }));
  }, [location.search]);

  const applyLink = `/creators/apply${location.search}`;
  // Show a real event's per-ticket amount when one is open; otherwise a plain $5 example.
  const exampleEvent = (program?.events || []).find((e) => e.perTicketCents);
  const exampleCents = exampleEvent?.perTicketCents || 500;
  const usd0 = (cents) => `$${(cents / 100) % 1 ? (cents / 100).toFixed(2) : cents / 100}`;

  return (
    <div className="cp">
      <CreatorBar
        right={
          <Link to="/creators/dashboard" className="cp-link">
            Creator sign in
          </Link>
        }
      />

      <main>
        <section className="cp-hero">
          <div className="cp-wrap cp-hero-grid">
            <div>
              <h1 className="display">
                <span className="line">Your influence.</span>
                <span className="line">Your audience.</span>
                <span className="line">Your earnings.</span>
              </h1>
              <p className="cp-lede">
                Turn your followers into ticket buyers and earn commissions on qualifying sales. Get your own QR code and link for
                every event you promote — when your audience buys through it, the sale is credited to you.
              </p>
              <div className="cp-actions">
                <Link to={applyLink} className="cp-btn" onClick={() => ttTrack("ClickButton", { description: "become_partner_hero" })}>
                  Become a Creator Partner
                </Link>
                <a href="#how" className="cp-btn ghost">
                  How it works
                </a>
              </div>
              <p className="cp-fine">Free to join. Commissions are paid only on verified ticket purchases; earnings aren't guaranteed.</p>
            </div>
            <CreatorPass handle="@you" code="CREATOR-1042-7Q3F" qrValue="https://instapass.shop/creators" meta="Sample pass · yours unlocks on approval" />
          </div>
        </section>

        <section className="cp-section" id="how">
          <div className="cp-wrap">
            <h2 className="display">How it works</h2>
            <p className="intro">No complicated setup. Just your audience, your code, and your earnings.</p>
            <ol className="cp-route">
              <li>
                <span className="stop">1</span>
                <h3>Apply in two minutes</h3>
                <p>Tell us your socials, your city and the events your audience shows up for.</p>
              </li>
              <li>
                <span className="stop">2</span>
                <h3>Get approved</h3>
                <p>Our team reviews every creator. Once you're in, your dashboard unlocks.</p>
              </li>
              <li>
                <span className="stop">3</span>
                <h3>Share your code</h3>
                <p>Get your own promo code, plus a link and QR code for every event. Post them on TikTok, Instagram, or text them to your people.</p>
              </li>
              <li>
                <span className="stop">4</span>
                <h3>Earn on verified sales</h3>
                <p>When someone buys with your promo code or through your link, the sale is credited to you and shows in your dashboard.</p>
              </li>
            </ol>
          </div>
        </section>

        <section className="cp-section">
          <div className="cp-wrap cp-split">
            <div>
              <h2 className="display">How you earn</h2>
              <p className="intro">
                Every event shows exactly what you earn per ticket before you promote it. Sell tickets with your code or link and you
                earn that amount on each one, once the sale is verified.
              </p>
              <ul className="cp-rules">
                <li>
                  <strong>A scan isn't a sale.</strong> Only completed, verified purchases count.
                </li>
                <li>
                  <strong>{program?.attributionDays || 7}-day window.</strong> A buyer who taps your link has {program?.attributionDays || 7} days to buy;
                  if they tap another creator's link after yours, the latest one gets the credit.
                </li>
                <li>
                  <strong>No self-referrals.</strong> Tickets you buy with your own code don't earn.
                </li>
                <li>
                  <strong>Refunds and cancellations don't earn.</strong> Commissions stay pending until the order is confirmed, then
                  our team approves them for payout.
                </li>
                <li>
                  <strong>Always disclose.</strong> Say you earn a commission — #ad or “I earn a commission on tickets bought with my
                  code” — right in the post.
                </li>
              </ul>
            </div>
            <div className="cp-receipt" aria-label="Example earnings">
              <div className="head">{exampleEvent ? `Example — ${exampleEvent.name}` : "Example"}</div>
              <div className="row">
                <span>You earn per ticket</span>
                <span>{usd0(exampleCents)}</span>
              </div>
              <div className="row">
                <span>Tickets sold with your code</span>
                <span>20</span>
              </div>
              <div className="row total">
                <span>You earn</span>
                <span>{usd0(exampleCents * 20)}</span>
              </div>
              <div className="row sub">
                <span>Each event card shows its own amount.</span>
                <span />
              </div>
            </div>
          </div>
        </section>

        <section className="cp-section">
          <div className="cp-wrap">
            <h2 className="display">Events open now</h2>
            <p className="intro">These are events InstaPass has cleared for creators to promote. New ones are added as they're authorized.</p>
            {!program ? (
              <p className="cp-fine">Loading events…</p>
            ) : program.events.length ? (
              <div className="cp-events">
                {program.events.map((e) => (
                  <article className="cp-event" key={e.id}>
                    <div className="art" style={e.imageUrl ? { backgroundImage: `url(${e.imageUrl})` } : undefined}>
                      {e.date && <span className="date">{fmtDate(e.date, { month: "short", day: "numeric" })}</span>}
                    </div>
                    <div className="body">
                      <h3>{e.name}</h3>
                      <div className="venue">{[e.venue, e.city].filter(Boolean).join(", ")}</div>
                      {e.perTicketCents ? (
                        <div className="rate">
                          You earn <b>{usd0(e.perTicketCents)}</b> per ticket
                        </div>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="cp-empty">
                <h3>New events drop soon</h3>
                Apply now so you're approved and ready to share the moment the next one opens.
              </div>
            )}
          </div>
        </section>

        <section className="cp-section">
          <div className="cp-wrap cp-split">
            <div>
              <h2 className="display">HAILEY has your back</h2>
              <p className="intro">
                HAILEY is your InstaPass Intelligence Captain. Ask her how the program works now — once you're approved, she can pull
                up your sales, your codes and where every commission stands, straight from your account.
              </p>
              <Link to={applyLink} className="cp-btn" onClick={() => ttTrack("ClickButton", { description: "become_partner_hailey" })}>
                Become a Creator Partner
              </Link>
            </div>
            <HaileyPanel
              onAction={(a) => {
                if (a.type === "apply") window.location.assign(applyLink);
              }}
            />
          </div>
        </section>
      </main>

      <CreatorFooter />
      <div className="cp-sticky">
        <Link to={applyLink} className="cp-btn" onClick={() => ttTrack("ClickButton", { description: "become_partner_sticky" })}>
          Become a Creator Partner
        </Link>
      </div>
    </div>
  );
}
